import { IconArrowLeft, IconCog, IconTimes, IconWifiSlash } from '@7shifts/sous-chef-base';
import { useCurrentTime } from './LocationInfo/useCurrentTime';
import { useTranslation } from '@src/i18n';
import { useNetworkStatus } from '@src/context/NetworkStatusContext';
import './TopBar.css';

const STATUS_DATE_FORMAT = new Intl.DateTimeFormat(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric'
});
const STATUS_TIME_FORMAT = new Intl.DateTimeFormat(undefined, {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
});

interface TopBarProps {
    /** Show the left action button (gear or back). Pass false on unauthenticated routes. */
    showLeftAction?: boolean;
    /** Render a back arrow instead of the gear icon — for sub-pages like Settings. */
    showBack?: boolean;
    onLeftAction?: () => void;
    /** Status-mode: shows back arrow + date/time on left, close on right.
     *  Both controls call onStatusDismiss. Overrides showLeftAction/showBack. */
    statusMode?: boolean;
    onStatusDismiss?: () => void;
}

export function TopBar({
    showLeftAction = true,
    showBack = false,
    onLeftAction,
    statusMode = false,
    onStatusDismiss
}: TopBarProps) {
    const now = useCurrentTime();
    const { t } = useTranslation();
    const { isOnline } = useNetworkStatus();

    const offlineIndicator = !isOnline ? (
        <span
            className="top-bar__offline-indicator"
            role="status"
            aria-label={t('common.offline')}
        >
            <IconWifiSlash size="default" />
        </span>
    ) : null;

    if (statusMode) {
        return (
            <div className="top-bar">
                <button
                    className="top-bar__btn top-bar__btn--status-back"
                    type="button"
                    onClick={onStatusDismiss}
                    aria-label={t('common.goBack')}
                >
                    <IconArrowLeft size="default" />
                    <span className="top-bar__status-datetime">
                        <span>{STATUS_DATE_FORMAT.format(now)}</span>
                        <span>{STATUS_TIME_FORMAT.format(now)}</span>
                    </span>
                </button>
                {offlineIndicator}
                <button
                    className="top-bar__btn"
                    type="button"
                    onClick={onStatusDismiss}
                    aria-label={t('common.close')}
                >
                    <IconTimes size="default" />
                </button>
            </div>
        );
    }

    return (
        <div className="top-bar">
            {showLeftAction ? (
                <button
                    className="top-bar__btn"
                    type="button"
                    onClick={onLeftAction}
                    aria-label={
                        showBack ? t('common.goBack') : t('common.settings')
                    }
                >
                    {showBack ? (
                        <IconArrowLeft size="default" />
                    ) : (
                        <IconCog size="default" />
                    )}
                </button>
            ) : (
                <div className="top-bar__spacer" aria-hidden="true" />
            )}

            {offlineIndicator}

            {/*
             * TODO (API-4486): The close action is not yet defined.
             * Once the close protocol is decided, wire this button to the
             * appropriate hostBridge call. At that point also decide whether
             * to hide the button in embedding contexts where close is not
             * supported (e.g. a plain iFrame whose host has no close handler).
             */}
            <button
                className="top-bar__btn"
                type="button"
                aria-label={t('common.close')}
            >
                <IconTimes size="default" />
            </button>
        </div>
    );
}
